import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../components/Navbar';
import { getStoreImagesForName } from '../data/storeImages';
import { updatedProductData } from '../data/productData';

// Luxury Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
`;

// Main Container
const StoreContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  color: #1a1a1a;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow-x: hidden;
  padding-top: 0;
`;

// Navigation Bar
const NavigationBar = styled.nav`
  position: relative;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid #f5f5f5;
  padding: 1rem 0;
  transition: all 0.3s ease;
  margin-top: 0;
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 300;
  letter-spacing: -0.02em;
  color: #1a1a1a;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: #d4af37;
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    text-align: center;
    width: 100%;
  }
`;

const NavCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex: 1;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: row;
    gap: 0.5rem;
    width: 100%;
    justify-content: space-between;
  }
`;

const SearchBar = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 1024px) {
    width: 200px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 25px;
  background: #f5f5f5;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
  
  &::placeholder {
  color: #888888;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  background-image: url('/images/logos/search.webp');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  filter: brightness(0.5);
`;

const CategoryDropdown = styled.div`
  position: relative;
`;

const CategoryDropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 200px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  margin-top: 0.5rem;
`;

const CategoryDropdownItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  color: #1a1a1a;
  
  &:hover {
    background: #f5f5f5;
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const CategoryDropdownButton = styled.button`
  background: none;
  border: none;
  color: #1a1a1a;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: #f5f5f5;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.4rem 0.6rem;
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    justify-content: flex-end;
    width: auto;
  }
`;

const OrdersButton = styled.button`
  background: transparent;
  border: none;
  outline: none;
  color: #000000;
  padding: 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: transform 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
`;

const CartButton = styled.button`
  position: relative;
  background: none;
  border: none;
  outline: none;
  color: #1a1a1a;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  
  &:focus {
    outline: none;
  }
  transition: all 0.3s ease;
  width: 24px;
  height: 24px;
  background-image: url('/images/logos/pngwing.com (11).webp');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const CartBadge = styled.div`
    position: absolute;
  top: -5px;
  right: -5px;
  background: #d4af37;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  height: 80vh;
  min-height: 600px;
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 60vh;
    min-height: 400px;
    padding: 2rem 0;
  }
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 200;
  margin-bottom: 1.5rem;
  letter-spacing: -0.03em;
  color: #1a1a1a;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: #666666;
  font-weight: 300;
  margin-bottom: 3rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }
`;

const PrimaryButton = styled.button`
  background: #1a1a1a;
    color: #ffffff;
  border: none;
  padding: 1rem 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 2px;
  
  &:hover {
    background: #d4af37;
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(26, 26, 26, 0.2);
  }
  
  @media (max-width: 768px) {
      width: 100%;
    max-width: 280px;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 1rem 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 2px;
  
  &:hover {
    background: #1a1a1a;
    color: #ffffff;
    transform: translateY(-2px);
  }
    
  @media (max-width: 768px) {
      width: 100%;
    max-width: 280px;
  }
`;

// Featured Products Section
const FeaturedSection = styled.section`
  padding: 6rem 0;
  background: #ffffff;
  
  @media (max-width: 768px) {
    padding: 3rem 0;
  }
`;

const SectionContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 200;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
  color: #1a1a1a;
  
  @media (max-width: 768px) {
  font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1rem;
  color: #666666;
  font-weight: 300;
  max-width: 600px;
  margin: 0 auto 2rem auto;
`;

const ViewAllButton = styled.button`
  background: #1a1a1a;
  color: #ffffff;
  border: 1px solid #1a1a1a;
  padding: 0.75rem 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 2px;
  margin-top: 1rem;
  
  &:hover {
    background: #d4af37;
    border-color: #d4af37;
    transform: translateY(-1px);
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ProductCard = styled(motion.div)`
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.4s ease;
  cursor: pointer;
  position: relative;
  
  &:hover {
    border-color: #d4af37;
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  aspect-ratio: 3248 / 4330; /* 3:4 portrait to match source images */
  overflow: hidden;
  background: #0f0f0f;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover; /* fill without side gaps, keep ratio */
  object-position: center;
  transition: transform 0.4s ease;
  
  ${ProductCard}:hover & {
    transform: scale(1.02);
  }
`;

const ProductBadge = styled.div<{ type: 'new' | 'sale' | 'limited' }>`
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.5rem 1rem;
  background: ${props => 
    props.type === 'new' ? '#1a1a1a' :
    props.type === 'sale' ? '#d4af37' :
    '#000000'
  };
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  z-index: 2;
`;


const ProductInfo = styled.div`
  padding: 2rem;
`;

const ProductBrand = styled.div`
  font-size: 0.75rem;
  color: #888888;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.5rem;
`;

const ProductName = styled.h3`
  font-size: 1.125rem;
  font-weight: 400;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
  line-height: 1.3;
`;

const ProductPrice = styled.div`
  font-size: 1.25rem;
  font-weight: 300;
  color: #1a1a1a;
  margin-bottom: 1rem;
`;

const ProductRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Stars = styled.div`
  display: flex;
  gap: 0.125rem;
`;

const Star = styled.span<{ $filled: boolean }>`
  color: ${props => props.$filled ? '#d4af37' : '#e0e0e0'};
  font-size: 0.875rem;
`;

const RatingText = styled.span`
  font-size: 0.75rem;
  color: #888888;
`;

const ProductActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const AddToCartButton = styled.button<{ $isInCart?: boolean }>`
  width: 100%;
  background: ${props => props.$isInCart ? '#4caf50' : '#1a1a1a'};
  color: #ffffff;
  border: none;
  padding: 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: ${props => props.$isInCart ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  border-radius: 2px;
  opacity: ${props => props.$isInCart ? 0.7 : 1};
  
  &:hover {
    background: ${props => props.$isInCart ? '#4caf50' : '#333333'};
    transform: ${props => props.$isInCart ? 'none' : 'translateY(-2px)'};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const WishlistButton = styled.button`
  background: transparent;
  color: #888888;
  border: 1px solid #e0e0e0;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 2px;
  
  &:hover {
    color: #d4af37;
    border-color: #d4af37;
  }
`;

// Categories Section
const CategoriesSection = styled.section`
  padding: 6rem 0;
  background: #f5f5f5;
  
  @media (max-width: 768px) {
    padding: 3rem 0;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const CategoryCard = styled(motion.div)`
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const CategoryImage = styled.div<{ $bgImage: string }>`
  height: 200px;
  background: linear-gradient(45deg, #f0f0f0, #ffffff);
  background-image: ${props => `url(${props.$bgImage})`};
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
  }
`;

const CategoryContent = styled.div`
  padding: 2rem;
  text-align: center;
`;

const CategoryTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 400;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
`;

const CategoryDescription = styled.p`
  color: #666666;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
`;

const CategoryActionButton = styled.button`
  background: #1a1a1a;
  color: #ffffff;
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 2px;
  
  &:hover {
    background: #d4af37;
  }
`;

// Newsletter Section
const NewsletterSection = styled.section`
  padding: 4rem 0;
  background: #1a1a1a;
  color: #ffffff;
  
  @media (max-width: 768px) {
    padding: 3rem 0;
  }
`;

const NewsletterContent = styled.div`
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const NewsletterTitle = styled.h2`
  font-size: 2rem;
  font-weight: 200;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
  font-size: 1.5rem;
  }
`;

const NewsletterSubtitle = styled.p`
  color: #888888;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 1rem;
  max-width: 400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 100%;
  }
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 1rem;
  border: 1px solid #333333;
  background: #0a0a0a;
  color: #ffffff;
  font-size: 0.875rem;
  border-radius: 2px;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
  
  &::placeholder {
    color: #666666;
  }
`;

const NewsletterButton = styled.button`
  background: #d4af37;
  color: #1a1a1a;
  border: none;
  padding: 1rem 2rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 2px;
  
  &:hover {
    background: #ffffff;
  }
`;

// Product Data with actual images
interface Product {
  id: number;
  name: string;
  slug: string;
  brand: string;
  price: number;
  originalPrice?: number;
  image: string;
  images?: string[];
  category: string;
  badge?: 'new' | 'sale' | 'limited' | null;
  inStock: boolean;
  isSecondHand?: boolean;
  description?: string;
  specifications?: string[];
  rating?: number;
  reviews?: number;
}

// CURRENT PRODUCT-IMAGE MAPPING (PRESERVED - SAME AS ProductDetail.tsx):
/*
SHOES (productNumber -> fileIndex mapping):
1. Beymen Suede Penny Loafers → product1 (main: product1-3.jpeg)
2. Saint Laurent High-Top Sneakers → product2 (main: product2-4.jpeg)
3. New Balance Retro Runner → product4
4. Versace Jeans Couture Logo Sneakers → product5
5-15. Other shoes → product6-product16 (3 variants each)
16. Jimmy Choo "JC Logo" Sneakers → product18 images
17. adidas YEEZY Knit Sneaker → product19 images
18-19. Other mappings preserved
*/

// Utility function to create URL-friendly slugs from product names
const createSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens and spaces
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

const generateProducts = (): Product[] => {
  const products: Product[] = [];
  
  // Shoe products from Ayakkabı folder - All available products
  const shoeProducts = [
    { 
      id: 4, // productData.ts ID
      name: 'Versace Jeans Couture Logo Sneakers – Black/Gold (EU 42, Second-Hand)', 
      brand: 'Versace Jeans Couture', 
      price: 3000, 
      originalPrice: 340, 
      category: 'Shoes',
      description: 'Bold VJC sneakers with oversized VERSACE JEANS COUTURE branding across the knit upper. Lightweight, cushioned, and perfect for statement streetwear looks.',
      specifications: ['Upper: breathable engineered knit/mesh with logo print; synthetic overlays', 'Midsole: lightweight EVA/foam for everyday comfort', 'Outsole: rubber with traction pattern', 'Lace-up closure; pull tabs', 'Color: Black with gold logo', 'EU 42 (US 9 / UK 8) – standard width', 'Insole length approx. 27.5–28.0 cm', 'Condition: Very good - light wear on soles; uppers clean', 'Care: Spot clean knit by hand; avoid machine wash']
    },
    { 
      name: 'Giuseppe Zanotti High-Top – Croc Embossed (EU 42, Pre-Owned)', 
      brand: 'Giuseppe Zanotti', 
      price: 8500, 
      originalPrice: 620, 
      category: 'Shoes',
      description: 'Striking black croc-embossed high-tops with gold side zips and signature tongue logo. Sporty-elegant—perfect for statement looks.',
      specifications: ['Upper: Black croc-embossed leather', 'Closure: Laces + side zippers', 'Interior: Leather/textile, padded collar', 'Sole: White rubber cupsole', 'Branding: Signature metal logo on tongue', 'EU 42 (US 9 / UK 8) — standard width', 'Insole: approx. 27.5–28.0 cm', 'Condition: Very good - light wear on sole, minor creasing on leather', 'Care: Wipe with a soft cloth; condition leather occasionally']
    },
    { name: 'Dolce & Gabbana Court Sneakers – White/Gold/Black (EU 42, Pre-owned)', brand: 'Dolce & Gabbana', price: 8500, originalPrice: 899, category: 'Shoes' },
    { 
      name: 'Saint Laurent High-Top Sneakers — White (EU 42, Second-Hand)', 
      brand: 'Saint Laurent', 
      price: 9500, 
      originalPrice: 899, 
      category: 'Shoes',
      description: 'Clean, minimalist Saint Laurent high-tops in white leather with perforated panels and side logo. A sharp, easy pair for everyday city wear.',
      specifications: ['Upper: genuine leather with perforated quarters', 'Lace-up high-top profile, padded tongue & collar', 'Side branding: SAINT LAURENT', 'Cupsole: durable rubber with translucent foxing', 'Color: White / off-white', 'EU 42 (US 9 / UK 8) — standard width', 'Insole length approx. 27.5–28.0 cm', 'Condition: Good/Very good - light wear to outsole and foxing; uppers clean with minor creasing', 'Care: Wipe with damp cloth; use colorless leather cream']
    },
    { 
      name: 'New Balance Retro Runner – Black (EU 42, Second-Hand)', 
      brand: 'New Balance', 
      price: 1900, 
      originalPrice: 110, 
      category: 'Shoes',
      description: 'Lightweight New Balance sneakers in black suede/mesh—everyday comfort with that classic NB runner look.',
      specifications: ['Upper: suede + mesh panels (breathable, durable)', 'Cushioning: EVA/foam midsole for all-day comfort', 'Outsole: grippy rubber with traction lugs', 'Closure: lace-up; padded tongue & collar', 'Color: Black / charcoal', 'EU 42 (US 9 / UK 8) — standard width', 'Insole length approx. 27.5–28.0 cm', 'Condition: Very good - minor wear on outsole edges; uppers clean', 'Care: Brush suede with a soft brush; spot-clean mesh']
    },
    { 
      name: 'Camper Lace-Up Boots – Black (EU 42, Second-Hand)', 
      brand: 'Camper', 
      price: 1500, 
      originalPrice: 170, 
      category: 'Shoes',
      description: 'Minimal, city-ready boots with Camper\'s signature mix of comfort and utility. Smooth black leather, lace-up front, and a sturdy rubber sole for everyday wear.',
      specifications: ['Upper: smooth leather in black', 'Closure: lace-up front; back pull tab', 'Lining: comfortable textile/leather mix', 'Outsole: grippy rubber with low, stable profile', 'Round toe; clean, modern silhouette', 'EU 42 (US 9 / UK 8) – regular width', 'Approx. insole length 27.5–28.0 cm', 'Condition: Very good - minor creasing on leather and light outsole wear', 'Care: Brush off dust; wipe with damp cloth; condition leather occasionally']
    },
    { 
      name: 'Camper Ankle Boots – Black Leather (EU 42, Pre-owned)', 
      brand: 'Camper', 
      price: 1250, 
      originalPrice: 210, 
      category: 'Shoes',
      description: 'Understated, sturdy black leather ankle boots with a chunky ribbed rubber rand and pull-on tabs. Minimal design, everyday comfort.',
      specifications: ['Smooth black leather uppers', 'Pull-on style with dual heel/tongue tabs', 'Protective ribbed rubber rand & grippy outsole', 'Round toe, clean seams, low profile heel', 'Upper: leather', 'Lining: leather/textile', 'Outsole: rubber', 'EU 42 (US 9 / UK 8) — standard width', 'Insole approx. 27.5–28.0 cm', 'Condition: Very good - light creasing to leather, minor wear on outsole', 'Care: Wipe with a soft cloth; nourish leather periodically']
    },
    { name: 'DSQUARED2 Retro Runner – Black/White (EU 42, Pre-owned)', brand: 'DSQUARED2', price: 4450, originalPrice: 450, category: 'Shoes' },
    { 
      id: 1, // productData.ts ID
      name: 'Beymen Suede Penny Loafers — Taupe/Beige (EU 42, Second-Hand)', 
      brand: 'Beymen', 
      price: 2500, 
      originalPrice: 282, 
      category: 'Shoes',
      description: 'Elegant Beymen loafers in soft, premium suede with classic penny strap and clean almond toe. Versatile taupe tone pairs effortlessly with smart-casual looks.',
      specifications: ['Upper: genuine suede leather', 'Lining & insole: leather (Beymen stamped footbed)', 'Outsole: leather (low stacked heel)', 'Penny strap, apron stitching, slip-on convenience', 'Color: Taupe / light beige', 'EU 42 (US 9 / UK 8) — standard width', 'Approx. insole length 27.5–28.0 cm; heel height ~2.5 cm', 'Condition: Very good - light, even wear on sole; uppers clean with minor, normal suede rubbing', 'Care: Brush with a suede brush; use suede protector spray']
    },
    { name: 'Giuseppe Zanotti Woven Slip-On Loafers – Navy (EU 42, Pre-owned)', brand: 'Giuseppe Zanotti', price: 7500, originalPrice: 699, category: 'Shoes' },
    { name: 'Louis Vuitton Monogram Driver Loafers – Blue (EU 42, Pre-owned)', brand: 'Louis Vuitton', price: 12500, originalPrice: 799, category: 'Shoes' },
    { name: 'Gucci GG Leather Espadrilles – Blue (EU 42, Pre-owned)', brand: 'Gucci', price: 7500, originalPrice: 450, category: 'Shoes' },
    { name: 'Gucci GG Canvas Espadrilles – Beige/Blue (EU 42, Pre-owned)', brand: 'Gucci', price: 8500, originalPrice: 499, category: 'Shoes' },
    { name: 'Valentino Garavani Chunky Sneakers – White/Grey (EU 42, Pre-owned)', brand: 'Valentino Garavani', price: 10500, originalPrice: 799, category: 'Shoes' },
    { name: 'Gucci Rhyton Logo Sneakers – Off-White (EU 42, Pre-owned)', brand: 'Gucci', price: 7500, originalPrice: 799, category: 'Shoes' },
    { name: 'Jimmy Choo "JC Logo" Sneakers – Triple-White (EU 42, Pre-owned)', brand: 'Jimmy Choo', price: 6500, originalPrice: 699, category: 'Shoes' },
    { name: 'adidas YEEZY Knit Sneaker – Lime/Volt (EU 42, Pre-owned)', brand: 'adidas YEEZY', price: 4500, originalPrice: 520, category: 'Shoes' }
  ];
  
  // Clothing products from Kıyafet folder - All available products
  const clothingProducts = [
    { name: 'Balmain Padded Logo-Tape Gilet — Black', brand: 'Balmain', price: 10500, originalPrice: 750, category: 'Clothing' },
    { name: 'Lufian Lightweight Puffer Gilet — Black (Second-Hand)', brand: 'Lufian', price: 1250, originalPrice: 5750, category: 'Jackets' },
    { name: 'Sandro Paris – "Mr. Strong" Crewneck Sweatshirt (Second-Hand)', brand: 'Sandro Paris', price: 5500, originalPrice: 399, category: 'Clothing' },
    { name: 'Just Cavalli – Graphic Crewneck Sweatshirt (Second-Hand)', brand: 'Just Cavalli', price: 5500, originalPrice: 350, category: 'Clothing' },
    { name: 'Damat Tween — Classic Blazer / Navy (EU 52, Second-Hand)', brand: 'Damat Tween', price: 7500, originalPrice: 450, category: 'Clothing' },
    { name: 'Just Cavalli — Logo Crewneck Sweatshirt, Beige (Size M, Second-Hand)', brand: 'Just Cavalli', price: 9500, originalPrice: 490, category: 'Clothing' },
    { name: 'Gucci — Web-Trim Cotton Piqué Polo, Navy (Second-Hand)', brand: 'Gucci', price: 7500, originalPrice: 550, category: 'T-Shirts' },
    { name: 'Balmain — Logo Piqué Polo, Black (Second-Hand)', brand: 'Balmain', price: 3500, originalPrice: 299, category: 'T-Shirts' },
    { name: 'Gucci — GG Tape Cotton Piqué Polo, White/Black (Second-Hand)', brand: 'Gucci', price: 7000, originalPrice: 520, category: 'T-Shirts' },
    { name: 'Just Cavalli — Graffiti Logo Crewneck Sweatshirt, Beige (Second-Hand)', brand: 'Just Cavalli', price: 9500, originalPrice: 490, category: 'Clothing', isSecondHand: true },
    { name: 'HUGO (by Hugo Boss) — "HEROES" Crewneck Sweatshirt, Black (Size M, Second-Hand)', brand: 'HUGO', price: 3200, originalPrice: 150, category: 'Clothing' },
    { name: 'Versace Jeans Couture — Baroque Medallion Crewneck Sweatshirt, Black (Size M, Second-Hand)', brand: 'Versace Jeans Couture', price: 7500, originalPrice: 530, category: 'Clothing' },
    { name: 'Versace Jeans Couture — Eagle Baroque Crewneck Sweatshirt, Black/Gold (Size M, Second-Hand)', brand: 'Versace Jeans Couture', price: 6500, originalPrice: 440, category: 'Clothing' },
    { name: 'Versace Jeans Couture — Patch Logo Crewneck Sweatshirt, Black (Size M, Second-Hand)', brand: 'Versace Jeans Couture', price: 8500, originalPrice: 520, category: 'Clothing' },
    { name: 'Just Cavalli — Graffiti Script Crewneck Sweatshirt, Black/White (Size M, Second-Hand)', brand: 'Just Cavalli', price: 4500, originalPrice: 350, category: 'Clothing' },
    { name: 'Versace Jeans Couture — Multicolor Logo Crewneck Sweatshirt, Black (Size M, Second-Hand)', brand: 'Versace Jeans Couture', price: 8500, originalPrice: 520, category: 'Clothing' },
    { name: 'PS Paul Smith — Chest-Patch Crewneck Sweatshirt, Black (Size M, Second-Hand)', brand: 'PS Paul Smith', price: 5500, originalPrice: 360, category: 'Clothing' },
    { name: 'Just Cavalli — Skull Graphic Crewneck Sweatshirt, Black (Size M, Second-Hand)', brand: 'Just Cavalli', price: 11500, originalPrice: 690, category: 'Clothing' },
    { name: 'Moschino — Logo Patch Hoodie, Black (Size M, Second-Hand)', brand: 'Moschino', price: 5500, originalPrice: 320, category: 'Clothing' },
    { name: 'HUGO (by Hugo Boss) — Reverse Box Logo Crewneck Sweatshirt, Black (Size M, Second-Hand)', brand: 'HUGO', price: 3500, originalPrice: 190, category: 'Clothing' },
    { name: 'Just Cavalli — Logo Crewneck Sweatshirt, Black/Beige (Size M, Second-Hand)', brand: 'Just Cavalli', price: 6500, originalPrice: 360, category: 'Clothing' },
    { name: 'DSQUARED2 — Distressed Logo Hoodie, Olive/Camo (Size M, Second-Hand)', brand: 'DSQUARED2', price: 4500, originalPrice: 390, category: 'Clothing' },
    { name: 'Versace Jeans Couture — Embossed/Gold Logo Crewneck Sweatshirt, Black (Size M, Second-Hand)', brand: 'Versace Jeans Couture', price: 5500, originalPrice: 420, category: 'Clothing' },
    { name: 'Vakko — Anchor Patch Cotton Piqué Polo, Black (Size L, Second-Hand)', brand: 'Vakko', price: 2500, originalPrice: 190, category: 'Clothing' },
    { name: 'Vakko — Striped Cotton Shirt, Beige/White (Size L, Second-Hand)', brand: 'Vakko', price: 3500, originalPrice: 240, category: 'Clothing' },
    { name: 'HUGO (by Hugo Boss) — Silver Sequin Dinner Blazer, Black Shawl Lapel (EU 48, Second-Hand)', brand: 'HUGO', price: 4000, originalPrice: 290, category: 'Clothing' },
    { name: 'Beymen — Houndstooth Wool-Blend Blazer, Grey (EU 52, Second-Hand)', brand: 'Beymen', price: 8500, originalPrice: 490, category: 'Clothing' },
    { name: 'Abdullah Kiğılı Exclusive — Checked Wool-Blend Blazer, Navy (EU 52, Second-Hand)', brand: 'Abdullah Kiğılı Exclusive', price: 6500, originalPrice: 320, category: 'Clothing' },
    { name: 'Abdullah Kiğılı Exclusive — Windowpane Wool-Blend Blazer, Grey/Navy (EU 52, Second-Hand)', brand: 'Abdullah Kiğılı Exclusive', price: 6500, originalPrice: 320, category: 'Clothing' },
    { name: 'Just Cavalli — "West Coast" Graphic Hoodie, Beige (Size M, Second-Hand)', brand: 'Just Cavalli', price: 7500, originalPrice: 370, category: 'Clothing' },
    { name: 'Porsche Design — Quilted Stand-Collar Jacket, Black (EU 52, Second-Hand)', brand: 'Porsche Design', price: 4500, originalPrice: 280, category: 'Jackets' },
    { name: 'Alpha Industries — B-15 Inspired Flight Jacket, Sage Green (Size M, Second-Hand)', brand: 'Alpha Industries', price: 8500, originalPrice: 360, category: 'Jackets' },
    { name: 'HUGO BOSS — Wool-Blend Tweed Blazer, Dark Brown/Black (EU 52, Second-Hand)', brand: 'HUGO BOSS', price: 8500, originalPrice: 399, category: 'Clothing' },
    { name: 'Fred Perry — Taped Track Jacket, Dark Green/Gold (Size L, Second-Hand)', brand: 'Fred Perry', price: 1800, originalPrice: 115, category: 'Jackets' },
    { name: 'Balmain — Monogram Zip Bomber Jacket, Black (Size L, Second-Hand)', brand: 'Balmain', price: 7500, originalPrice: 490, category: 'Jackets' },
    { name: 'Balmain — Logo T-Shirt with Monogram Sleeve Trim, Black (Size L, Second-Hand)', brand: 'Balmain', price: 4500, originalPrice: 300, category: 'T-Shirts' },
    { name: 'Balmain — Logo T-Shirt & Swim Shorts Set, Black (T-Shirt M, Shorts L, Second-Hand)', brand: 'Balmain', price: 5500, originalPrice: 350, category: 'Clothing' },
    { name: 'Balmain — Paris Logo T-Shirt, Black (Size M, Second-Hand)', brand: 'Balmain', price: 4500, originalPrice: 300, category: 'T-Shirts' },
    { name: 'Balmain — Crest Patch Cotton Piqué Polo, Black (Size M, Second-Hand)', brand: 'Balmain', price: 3200, originalPrice: 190, category: 'T-Shirts' },
    { name: 'Sertaç Taşdelen — Summer Set (Top & Trousers), Ecru/Stone (Size L, Second-Hand)', brand: 'Sertaç Taşdelen', price: 6500, originalPrice: 450, category: 'Special' },
    { name: 'Sertaç Taşdelen — Summer Set (V-Neck Top & Trousers), White (Size L, Second-Hand)', brand: 'Sertaç Taşdelen', price: 6500, originalPrice: 450, category: 'Special' },
    { name: 'Represent — Oversize T-Shirt + 2x Shorts Set, Cream/Beige + Black (Size L, Second-Hand)', brand: 'Represent', price: 4500, originalPrice: 180, category: 'Special' },
    { name: 'Baldessarini — Short-Sleeve Camp-Collar Shirt, Bird/Floral Print (Size M, Second-Hand)', brand: 'Baldessarini', price: 4600, originalPrice: 190, category: 'Shirts' },
    { name: 'Sandro — Short-Sleeve Print Shirt (Size M, Second-Hand)', brand: 'Sandro', price: 1500, originalPrice: 130, category: 'Shirts' },
    { name: 'AllSaints — Palm Collage Camp-Collar Shirt (L, Second-Hand)', brand: 'AllSaints', price: 1500, originalPrice: 130, category: 'Shirts' },
    { name: 'Balmain Logo T-Shirt – Black (Pre-owned)', brand: 'Balmain', price: 5500, originalPrice: 320, category: 'T-Shirts' },
    { name: 'Just Cavalli Short-Sleeve Shirt – Python Print (Size M, Pre-owned)', brand: 'Just Cavalli', price: 5500, originalPrice: 320, category: 'Shirts' },
    { name: 'Versace Jeans Couture Oversize Shirt – White (Size L, Pre-owned)', brand: 'Versace Jeans Couture', price: 4500, originalPrice: 390, category: 'Shirts' }
  ];
  
  // Add shoe products with variants
  shoeProducts.forEach((product, index) => {
    const productNumber = product.id || (index + 1); // Use product.id if available, otherwise index + 1
    // Map file index to skip product4 assets and also map product3 -> product4
    let fileIndex = productNumber;
    if (fileIndex === 3) {
      fileIndex = 4;
    } else if (fileIndex >= 4) {
      fileIndex = fileIndex + 1;
    }
    const baseImage = `product${fileIndex}`;
    
    // Check which variants exist for this (mapped) file index based on actual files
    let variants = [`${baseImage}.jpeg`]; // Always include main image
    
    // Add variant images that actually exist
    if (productNumber <= 19) { // Products 1-19 exist
      if (productNumber === 1) {
        // product1 has 4 variants - use product1-3.jpeg as main image
        variants = [`${baseImage}-3.jpeg`, `${baseImage}.jpeg`, `${baseImage}-1.jpeg`, `${baseImage}-2.jpeg`, `${baseImage}-4.jpeg`];
      } else if (productNumber === 2) {
        // product2 has 4 variants - use product2-2.jpeg as main image (Saint Laurent High-Top Sneakers-2.webp)
        variants = [`${baseImage}-2.jpeg`, `${baseImage}.jpeg`, `${baseImage}-1.jpeg`, `${baseImage}-3.jpeg`, `${baseImage}-4.jpeg`];
      } else if (productNumber === 4) {
        // product4 has 3 variants
        variants.push(`${baseImage}-1.jpeg`, `${baseImage}-2.jpeg`, `${baseImage}-3.jpeg`);
      } else if (productNumber >= 5 && productNumber <= 15) {
        // products 5-15 have 3 variants each
        if (productNumber === 6) {
          // Giuseppe Zanotti High-Top - use -1.jpeg as main image
          variants = [`${baseImage}-1.jpeg`, `${baseImage}.jpeg`, `${baseImage}-2.jpeg`, `${baseImage}-3.jpeg`];
        } else if (productNumber === 9) {
          // Dolce & Gabbana Court Sneakers - use -1.jpeg as main image
          variants = [`${baseImage}-1.jpeg`, `${baseImage}.jpeg`, `${baseImage}-2.jpeg`, `${baseImage}-3.jpeg`, `${baseImage}-4.jpeg`];
        } else if (productNumber === 12) {
          // Gucci Rhyton Logo Sneakers - use -2.jpeg as main image
          variants = [`${baseImage}-2.jpeg`, `${baseImage}.jpeg`, `${baseImage}-1.jpeg`, `${baseImage}-3.jpeg`, `${baseImage}-4.jpeg`];
        } else {
          variants.push(`${baseImage}-1.jpeg`, `${baseImage}-2.jpeg`, `${baseImage}-3.jpeg`);
        }
      } else if (productNumber === 16) {
        // product16 uses product18 images
        variants = [`product18.jpeg`, `product18-1.jpeg`, `product18-2.jpeg`, `product18-3.jpeg`];
      } else if (productNumber === 17) {
        // product17 - adidas YEEZY uses product19 images
        variants = [`product19.jpeg`, `product19-1.jpeg`, `product19-2.jpeg`, `product19-3.jpeg`, `product19-4.jpeg`];
      } else if (productNumber === 18) {
        // product18 has 3 variants
        variants.push(`${baseImage}-1.jpeg`, `${baseImage}-2.jpeg`, `${baseImage}-3.jpeg`);
      } else if (productNumber === 19) {
        // product19 - Jimmy Choo uses product18 images
        variants = [`product18.jpeg`, `product18-1.jpeg`, `product18-2.jpeg`, `product18-3.jpeg`];
      }
    }
    
    const storeImgsShoes = getStoreImagesForName(product.name, 'Shoes');
    const resolvedImagesShoes = storeImgsShoes.length > 0 ? storeImgsShoes : variants.map(variant => `/images/products/Ayakkabı/${variant}`);
    products.push({
      id: products.length + 1,
      name: product.name,
      slug: createSlug(product.name),
      brand: product.brand,
      price: product.price,
      originalPrice: product.originalPrice,
      image: resolvedImagesShoes[0],
      images: resolvedImagesShoes,
      category: product.category,
      rating: Math.round((Math.random() * 1.5 + 3.5) * 10) / 10,
      reviews: Math.floor(Math.random() * 200) + 20,
      badge: Math.random() > 0.7 ? 'new' : Math.random() > 0.8 ? 'sale' : null,
      inStock: Math.random() > 0.1
    });
  });
  
  // Add clothing products with variants
  clothingProducts.forEach((product, index) => {
    const productNumber = index + 1;
    // Drop card for product6 entirely
    if (productNumber === 6) {
      return;
    }
    // Merge 41,42,44 into a single card: keep 41, drop 42 and 44
    if (productNumber === 42 || productNumber === 44) {
      return;
    }
    const baseImage = `product${productNumber}`;
    
    // Check which variants exist for this product
    let variants = [`${baseImage}.webp`]; // Always include main image
    
    // Add variant images if they exist
    if (productNumber <= 51) { // Only for products 1-51
      if (productNumber === 2) {
        // product2 has 3 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`, `${baseImage}-3.webp`);
      } else if (productNumber === 28) {
        // product28 has 3 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`, `${baseImage}-3.webp`);
      } else if (productNumber === 33) {
        // product33 has 2 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`);
      } else if (productNumber === 36) {
        // product36 has 2 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`);
      } else if (productNumber === 37) {
        // product37 has 2 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`);
      } else if (productNumber === 38) {
        // product38 has 2 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`);
      } else if (productNumber === 39) {
        // product39 has 2 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`);
      } else if (productNumber === 40) {
        // product40 has 6 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`, `${baseImage}-3.webp`, `${baseImage}-4.webp`, `${baseImage}-5.webp`, `${baseImage}-6.webp`);
      } else if (productNumber === 41) {
        // product41 has 2 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`);
      } else if (productNumber === 42) {
        // product42 has 1 variant
        variants.push(`${baseImage}-1.webp`);
      } else if (productNumber === 43) {
        // product43 has 2 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`);
      } else if (productNumber === 44) {
        // product44 has 1 variant
        variants.push(`${baseImage}-1.webp`);
      } else if (productNumber === 45) {
        // product45 has 2 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`);
      } else if (productNumber === 46) {
        // product46 has 2 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`);
      } else if (productNumber === 47) {
        // product47 has 2 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`);
      } else if (productNumber === 48) {
        // product48 has 2 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`);
      } else if (productNumber === 49) {
        // product49 has 3 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`, `${baseImage}-3.webp`);
      } else if (productNumber === 50) {
        // product50 has 3 variants
        variants.push(`${baseImage}-1.webp`, `${baseImage}-2.webp`, `${baseImage}-3.webp`);
      } else if (productNumber === 51) {
        // product51 has 1 variant (prodcut51-1.webp - note the typo in filename)
        variants.push(`prodcut${productNumber}-1.webp`);
      } else {
        // Most products have 1 variant
        variants.push(`${baseImage}-1.webp`);
      }
    }
    
    // Limit to first 2 variants for display
    variants = variants.slice(0, 2);

    // Direct mapping: each clothing product uses its own data
    let sourceIndex = index;
    
    // Apply the same specific mappings as ProductDetail for consistency
    if (productNumber === 57) {
      sourceIndex = 40; // Sertaç Taşdelen Summer Set
    } else if (productNumber === 46) {
      sourceIndex = 43; // Baldessarini Camp-Collar Shirt  
    } else if (productNumber === 45) {
      sourceIndex = 42; // Represent Oversize T-Shirt Set
    } else if (productNumber === 43) {
      sourceIndex = 46; // AllSaints Palm Collage Shirt
    }
    const src = clothingProducts[sourceIndex] || product;

    // Override display texts for specific clothing items
    let displayName = src.name;
    let displayBrand = src.brand;
    if (productNumber === 46) {
      displayName = 'Baldessarini — Short-Sleeve Camp-Collar Shirt, Bird/Floral Print (Size M, Second-Hand)';
      displayBrand = 'Baldessarini';
    } else if (productNumber === 45) {
      displayName = 'Represent — Oversize T-Shirt + 2x Shorts Set, Cream/Beige + Black (Size L, Second-Hand)';
      displayBrand = 'Represent';
    } else if (productNumber === 43) {
      displayName = 'AllSaints — Palm Collage Camp-Collar Shirt (L, Second-Hand)';
      displayBrand = 'AllSaints';
    }

    const storeImgsClothing = getStoreImagesForName(displayName, 'Clothing');
    const resolvedImagesClothing = storeImgsClothing.length > 0 ? storeImgsClothing : variants.map(variant => `/images/products/Kıyafet/${variant}`);
    products.push({
      id: 1000 + productNumber, // Use offset + productNumber for clothing to avoid ID conflicts
      name: displayName,
      slug: createSlug(displayName),
      brand: displayBrand,
      price: src.price,
      originalPrice: src.originalPrice,
      image: resolvedImagesClothing[0],
      images: resolvedImagesClothing,
      category: src.category,
      rating: Math.round((Math.random() * 1.5 + 3.5) * 10) / 10,
      reviews: Math.floor(Math.random() * 200) + 20,
      badge: Math.random() > 0.7 ? 'new' : Math.random() > 0.8 ? 'sale' : null,
      inStock: Math.random() > 0.1,
      isSecondHand: src.isSecondHand || false
    });
  });
  
  return products;
};

// Convert updatedProductData to Product format with images
const allProducts: Product[] = updatedProductData.map((src) => {
  const storeImgs = getStoreImagesForName(src.name, src.category);
  const folderName = src.category === 'Shoes' ? 'Ayakkabı' : 'Kıyafet';
  const resolvedImages = storeImgs.length > 0 ? storeImgs : [`/images/products/${folderName}/product${src.id}.webp`];
  
  return {
    id: src.id,
    name: src.name,
    slug: src.slug,
    brand: src.brand,
    price: src.salePrice,
    originalPrice: src.newPrice > 0 ? src.newPrice : undefined,
    image: resolvedImages[0],
    images: resolvedImages,
    category: src.category,
    badge: null,
    inStock: true,
    isSecondHand: src.isSecondHand || false,
    description: src.description,
    specifications: src.specifications
  };
});

const featuredProducts = allProducts
  .sort((a, b) => b.price - a.price) // Sort by price descending
  .slice(0, 12); // Show 12 most expensive as featured

const categories = [
  {
    id: 'Clothing',
    title: 'Clothing',
    description: 'Luxury fashion and accessories',
    image: '/images/store/Giyim/Balmain Padded Logo-Tape Gilet.webp'
  },
  {
    id: 'Jackets',
    title: 'Jackets',
    description: 'Premium jacket collection',
    image: '/images/store/Giyim/Alpha Industries — B-15 Inspired Flight Jacket, Sage Green.webp'
  },
  {
    id: 'Shirts',
    title: 'Shirts',
    description: 'Elegant shirt collection',
    image: '/images/store/Giyim/Versace Jeans Couture Oversize Shirt – White.webp'
  },
  {
    id: 'T-Shirts',
    title: 'T-Shirts & Polos',
    description: 'Casual tees and polo shirts',
    image: '/images/store/Giyim/Balmain — Paris Logo T-Shirt, Black.webp'
  },
  {
    id: 'Special',
    title: 'Special Sets',
    description: 'Exclusive matching sets',
    image: '/images/store/Giyim/Sertaç Taşdelen — Summer Set .webp'
  },
  {
    id: 'Shoes',
    title: 'Shoes',
    description: 'Premium footwear collection',
    image: '/images/store/Ayakkabı/Beymen Suede Penny Loafers — Taupe:Beige.webp'
  },
  {
    id: 'Perfume',
    title: 'Perfume',
    description: 'Exclusive fragrance collection',
    image: '/images/products/image00003.webp'
  },
  {
    id: 'Watches',
    title: 'Watches',
    description: 'Elite timepieces and jewelry',
    image: '/images/products/image00004.webp'
  },
  {
    id: 'Jewelry',
    title: 'Jewelry',
    description: 'Elegant jewelry and accessories',
    image: '/images/products/image00005.webp'
  },
  {
    id: 'Accessories',
    title: 'Accessories',
    description: 'Premium accessories and decor',
    image: '/images/products/image00006.webp'
  }
];

const Store: React.FC = () => {
  const { cartItems, cartCount, addToCart } = useCart();
  const { language, setLanguage } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState(featuredProducts);
  const [soldProductIds, setSoldProductIds] = useState<number[]>([]);
  const [reservedProductIds, setReservedProductIds] = useState<number[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Satılan ve rezerve ürünleri getir
  useEffect(() => {
    const fetchSoldProducts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/sold-products`);
        const data = await response.json();
        if (data.success) {
          setSoldProductIds(data.sold_product_ids);
        }
      } catch (error) {
        // Hata sessizce yoksayılır
      }
    };
    
    const fetchReservedProducts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/reserved-products`);
        const data = await response.json();
        if (data.success) {
          setReservedProductIds(data.reserved_product_ids);
        }
      } catch (error) {
        // Hata sessizce yoksayılır
      }
    };
    
    fetchSoldProducts();
    fetchReservedProducts();
  }, []);

  const handleAddToCart = async (productId: number) => {
    // Ürün zaten sepette mi kontrol et
    const isInCart = cartItems.some(item => item.product_id === productId);
    
    if (isInCart) {
      return; // Zaten sepette, hiçbir şey yapma
    }
    
    // Ürün bilgilerini bul
    const product = featuredProducts.find(p => p.id === productId);
    
    if (!product) {
      return;
    }
    
    // Sepete ekle
    await addToCart({
      product_id: product.id,
      product_name: product.name,
      product_image: product.image,
      price: product.price,
      quantity: 1,
      category: product.category
    });
  };

  const handleProductClick = (product: any) => {
    const category = product.category as string | undefined;
    const categorySlug = category ? category.toLowerCase().replace('-', '') : undefined;
    
    if (categorySlug) {
      navigate(`/store/${categorySlug}/${product.slug}`);
    } else {
      navigate(`/store/${product.slug}`);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  // Preload product images in background to avoid lazy loading flicker
  useEffect(() => {
    filteredProducts.forEach(p => {
      [...(p.images || []), p.image]
        .filter(Boolean)
        .forEach(src => {
          const img = new Image();
          img.src = src as string;
        });
    });
  }, [filteredProducts]);

  const filterProducts = () => {
    let filtered = allProducts;
    
    // Satılan ürünleri filtrele
    filtered = filtered.filter(product => !soldProductIds.includes(product.id));
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => 
        product.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredProducts(filtered); // Show all results at once
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setIsCategoryDropdownOpen(false);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} $filled={i < Math.floor(rating)}>
        ★
      </Star>
    ));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initialize filtered products and update when soldProductIds changes
  useEffect(() => {
    // Satılmış ürünleri hariç tut ve en pahalı 12 ürünü göster
    const availableProducts = allProducts
      .filter(product => !soldProductIds.includes(product.id))
      .sort((a, b) => b.price - a.price)
      .slice(0, 12);
    setFilteredProducts(availableProducts);
  }, [soldProductIds]);

  // Filter products when category changes
  useEffect(() => {
    if (selectedCategory !== 'all' || searchQuery.trim()) {
      filterProducts();
    }
  }, [selectedCategory]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      filterProducts();
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, soldProductIds]);

  return (
    <>
      <Helmet>
        <title>Elite Store - Premium Luxury Collection</title>
        <meta name="description" content="Discover our exclusive collection of luxury items. Premium quality, exceptional craftsmanship." />
        <meta name="keywords" content="luxury, premium, fashion, accessories, jewelry, watches, perfume" />
      </Helmet>
      
      <StoreContainer>
        {/* Navigation */}
        <NavigationBar>
          <NavContainer>
            <Logo onClick={() => navigate('/store')}>STORE</Logo>
            
            <NavCenter>
              <SearchBar>
                <SearchIcon></SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                />
              </SearchBar>
            </NavCenter>
            
            <NavRight>
              <OrdersButton onClick={() => navigate('/')}>
                {language === 'tr' ? 'Ana Sayfa' : 'Home'}
              </OrdersButton>
              <OrdersButton onClick={() => navigate('/orders')}>
                {language === 'tr' ? 'Siparişlerim' : 'My Orders'}
              </OrdersButton>
              <div style={{ position: 'relative' }}>
                <OrdersButton onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}>
                  🌐 {language.toUpperCase()}
                </OrdersButton>
                {isLanguageDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    minWidth: '140px',
                    zIndex: 1000
                  }}>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setLanguage('tr'); setIsLanguageDropdownOpen(false); }} 
                      style={{
                        width: '100%',
                        padding: '12px 20px',
                        border: 'none',
                        background: language === 'tr' ? '#d4af37' : 'white',
                        color: language === 'tr' ? 'white' : '#1a1a1a',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: language === 'tr' ? '600' : '400',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        if (language !== 'tr') {
                          e.currentTarget.style.background = '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (language !== 'tr') {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      🇹🇷 Türkçe
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setLanguage('en'); setIsLanguageDropdownOpen(false); }} 
                      style={{
                        width: '100%',
                        padding: '12px 20px',
                        border: 'none',
                        background: language === 'en' ? '#d4af37' : 'white',
                        color: language === 'en' ? 'white' : '#1a1a1a',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: language === 'en' ? '600' : '400',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        if (language !== 'en') {
                          e.currentTarget.style.background = '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (language !== 'en') {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      🇬🇧 English
                    </button>
                  </div>
                )}
              </div>
              <CartButton onClick={() => navigate('/cart')}>
                {cartCount > 0 && <CartBadge>{cartCount}</CartBadge>}
              </CartButton>
            </NavRight>
          </NavContainer>
        </NavigationBar>

        {/* Featured Products */}
        <FeaturedSection>
          <SectionContainer>
            <SectionHeader>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <SectionTitle>{language === 'tr' ? 'Öne Çıkan Ürünler' : 'Featured Products'}</SectionTitle>
                <SectionSubtitle>
                  {language === 'tr' 
                    ? 'Zarafet ve sofistikeyi tanımlayan özenle seçilmiş lüks ürünler'
                    : 'Handpicked luxury items that define elegance and sophistication'}
                </SectionSubtitle>
                <ViewAllButton onClick={() => navigate('/products')}>
                  {language === 'tr' ? 'Tüm Ürünleri Görüntüle' : 'View All Products'}
                </ViewAllButton>
              </motion.div>
            </SectionHeader>
            
            <ProductsGrid>
                {filteredProducts.slice(0, 12).map((product, index) => {
                  const isReserved = reservedProductIds.includes(product.id);
                  return (
                  <ProductCard
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                  >
                    <ProductImageContainer>
                      <ProductImage src={product.image} alt={product.name} loading="lazy" />
                      {isReserved && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%) rotate(-15deg)',
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          color: '#fff',
                          textTransform: 'uppercase',
                          letterSpacing: '3px',
                          textShadow: '0 0 15px rgba(0,0,0,0.8), 0 0 30px rgba(0,0,0,0.6)',
                          zIndex: 2,
                          pointerEvents: 'none'
                        }}>
                          {language === 'tr' ? 'REZERVELİ' : 'RESERVED'}
                        </div>
                      )}
                    </ProductImageContainer>
                    
                    <ProductInfo>
                      <ProductBrand>{product.brand}</ProductBrand>
                      <ProductName>{product.name}</ProductName>
                      <ProductPrice>₺{product.price.toLocaleString()}</ProductPrice>
                      
                      <ProductActions>
                        {(() => {
                          const isInCart = cartItems.some(item => item.product_id === product.id);
                          return (
                            <AddToCartButton 
                              onClick={() => handleAddToCart(product.id)}
                              disabled={isInCart}
                              $isInCart={isInCart}
                            >
                              {isInCart 
                                ? (language === 'tr' ? '✓ Sepete Eklendi' : '✓ Added to Cart') 
                                : (language === 'tr' ? 'Sepete Ekle' : 'Add to Cart')}
                            </AddToCartButton>
                          );
                        })()}
                      </ProductActions>
                    </ProductInfo>
                  </ProductCard>
                  );
                })}
            </ProductsGrid>
          </SectionContainer>
        </FeaturedSection>

        {/* Newsletter */}
        <NewsletterSection>
          <NewsletterContent>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <NewsletterTitle>Stay Updated</NewsletterTitle>
              <NewsletterSubtitle>
                Subscribe to receive exclusive offers and new arrivals
              </NewsletterSubtitle>
              <NewsletterForm onSubmit={handleSearch}>
                <NewsletterInput
                  type="email"
                  placeholder="Enter your email"
                  required
                />
                <NewsletterButton type="submit">Subscribe</NewsletterButton>
              </NewsletterForm>
            </motion.div>
          </NewsletterContent>
        </NewsletterSection>
      </StoreContainer>
    </>
  );
};

export default Store;